import { useMemo, useState } from "react";
import { Calendar, Clock, CheckCircle2 } from "lucide-react";

const SLOTS = ["10:00", "10:30", "11:00", "11:30", "12:00", "12:30", "14:30", "15:00", "15:30", "16:00", "16:30", "17:00", "17:30"];

function fmtDateLabel(d) {
    return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
}
function fmtDateKey(d) {
    return d.toISOString().slice(0, 10);
}

export default function ConsultationScheduler({ value, onChange, testId = "consultation-scheduler" }) {
    // Build next 10 business days (skip Sunday)
    const days = useMemo(() => {
        const out = [];
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        let added = 0;
        let cursor = new Date(d);
        cursor.setDate(cursor.getDate() + 1); // start from tomorrow
        while (added < 10) {
            if (cursor.getDay() !== 0) {
                out.push(new Date(cursor));
                added += 1;
            }
            cursor.setDate(cursor.getDate() + 1);
        }
        return out;
    }, []);

    const [selectedDate, setSelectedDate] = useState(value ? value.split("T")[0] : "");
    const [selectedTime, setSelectedTime] = useState(value ? value.split("T")[1] || "" : "");

    const updateSlot = (date, time) => {
        setSelectedDate(date);
        setSelectedTime(time);
        if (date && time) {
            onChange?.(`${date}T${time}`);
        } else if (!date && !time) {
            onChange?.("");
        }
    };

    const onPickDate = (key) => {
        updateSlot(key, selectedTime);
    };
    const onPickTime = (t) => {
        if (selectedDate) updateSlot(selectedDate, t);
    };
    const onClear = () => updateSlot("", "");

    return (
        <div data-testid={testId} className="bg-[#F8FAFC] border-2 border-[#28406A] p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div className="flex items-center gap-3">
                    <span className="w-9 h-9 inline-flex items-center justify-center bg-[#F4A000] text-[#101A2C] flex-shrink-0">
                        <Calendar className="h-4 w-4" strokeWidth={1.75} />
                    </span>
                    <div>
                        <p className="overline">Book a 30-min Call</p>
                        <p className="font-display uppercase text-sm sm:text-base text-[#101A2C]">Pick a slot that works for you</p>
                    </div>
                </div>
                {(selectedDate || selectedTime) && (
                    <button type="button" onClick={onClear} data-testid="scheduler-clear" className="text-[10px] uppercase tracking-widest font-bold text-[#4B6390] hover:text-[#28406A] border-b border-current">
                        Clear slot
                    </button>
                )}
            </div>

            {/* Date pills */}
            <p className="overline mb-2">1. Choose a day</p>
            <div className="relative -mx-1 mb-5">
                <div
                    className="flex gap-2 overflow-x-auto pb-2 px-1 scroll-smooth snap-x snap-mandatory"
                    data-testid="scheduler-dates"
                    style={{ scrollbarWidth: "thin" }}
                >
                    {days.map((d) => {
                        const k = fmtDateKey(d);
                        const active = selectedDate === k;
                        return (
                            <button
                                key={k}
                                type="button"
                                onClick={() => onPickDate(k)}
                                data-testid={`scheduler-date-${k}`}
                                className={`flex-shrink-0 snap-start flex flex-col items-center justify-center min-w-[64px] sm:min-w-[80px] px-2.5 py-3 border-2 transition-all ${
                                    active
                                        ? "bg-[#28406A] text-white border-[#28406A]"
                                        : "bg-white text-[#101A2C] border-[#CBD5E1] hover:border-[#28406A]"
                                }`}
                            >
                                <span className={`text-[10px] uppercase tracking-widest font-bold ${active ? "text-[#F4A000]" : "text-[#4B6390]"}`}>
                                    {d.toLocaleDateString("en-IN", { weekday: "short" })}
                                </span>
                                <span className="font-display text-xl sm:text-2xl leading-none mt-1">{d.getDate()}</span>
                                <span className={`text-[9px] uppercase tracking-widest mt-1 ${active ? "text-[#CBD5E1]" : "text-[#4B6390]"}`}>
                                    {d.toLocaleDateString("en-IN", { month: "short" })}
                                </span>
                            </button>
                        );
                    })}
                </div>
                {/* Fade indicator on right edge to suggest horizontal scrollability */}
                <div className="pointer-events-none absolute top-0 right-0 bottom-2 w-10 bg-gradient-to-l from-[#F8FAFC] to-transparent" />
                <p className="text-[10px] text-[#4B6390] mt-1 sm:hidden">← Swipe to see more dates →</p>
            </div>

            {/* Time slots */}
            <p className="overline mb-2">2. Pick a time (IST)</p>
            <div className={`grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 ${!selectedDate ? "opacity-40 pointer-events-none" : ""}`} data-testid="scheduler-times">
                {SLOTS.map((t) => {
                    const active = selectedTime === t && !!selectedDate;
                    return (
                        <button
                            key={t}
                            type="button"
                            onClick={() => onPickTime(t)}
                            data-testid={`scheduler-time-${t}`}
                            className={`px-2 py-2.5 text-xs font-bold border transition-all whitespace-nowrap ${
                                active
                                    ? "bg-[#F4A000] text-[#101A2C] border-[#F4A000]"
                                    : "bg-white text-[#101A2C] border-[#CBD5E1] hover:border-[#28406A]"
                            }`}
                        >
                            <Clock className="h-3 w-3 inline-block mr-1 -mt-0.5" strokeWidth={1.75} />
                            {t}
                        </button>
                    );
                })}
            </div>

            {/* Confirmation */}
            {selectedDate && selectedTime && (
                <div className="mt-5 bg-emerald-50 border-l-4 border-emerald-600 p-4 flex items-start gap-3" data-testid="scheduler-confirmation">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" strokeWidth={1.75} />
                    <div>
                        <p className="text-xs uppercase tracking-widest font-bold text-emerald-700">Slot Selected</p>
                        <p className="font-display uppercase text-sm sm:text-base text-[#101A2C] mt-1">
                            {fmtDateLabel(new Date(selectedDate))} · {selectedTime} IST
                        </p>
                        <p className="text-[11px] text-[#4B6390] mt-1">
                            Our supply-chain expert will call you at this slot. Submit the form to confirm.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}
